import type { PropertySchema } from '@takeshape/schema';
import type { ImmutableMap, MaybeImmutableObject } from 'app/common/utils/immutable-types';
import { fromJS } from 'immutable';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getData } from '../../selectors.js';
import type { ResolveRef, SchemaFieldProps, SchemaFormSlice } from '../../types.js';
import { findMatchingSchema, getDiscriminatorValue } from '../../util/discriminator.js';
import { parseRef } from '../../util/refs.js';
import type { ObjectFieldHookProps } from './object-field.js';

function createRef(path: string[]): ImmutableMap<PropertySchema> {
  const currentRef = `#/${path.join('/')}`;
  return fromJS({ $ref: currentRef });
}

function isArray(schemaPath: string[], resolveRef: ResolveRef): boolean {
  return resolveRef(createRef(schemaPath.slice(0, -1))).get('type') === 'array';
}

export function useObjectField(props: SchemaFieldProps): ObjectFieldHookProps {
  const { schemaPath, resolveRef, schema, path, formName } = props;

  const valueSelector = useCallback((state: SchemaFormSlice) => getData(state, { path, formName }), [path, formName]);
  const value = useSelector(valueSelector);

  const discriminatorValue = getDiscriminatorValue(schema, value as MaybeImmutableObject | undefined);

  return useMemo(() => {
    let typeName;
    let resolvedSchemaPath = schemaPath;
    if (schemaPath.length > 1) {
      const parentSchema = resolveRef(createRef(schemaPath));
      const $ref = parentSchema.get('$ref');
      if ($ref) {
        ({ typeName, schemaPath: resolvedSchemaPath } = parseRef($ref));
      }
    }

    const isArrayItem = isArray(schemaPath, resolveRef);

    const oneOf = schema.get('oneOf');
    if (oneOf) {
      const refSchema = oneOf.size === 1 ? oneOf.get(0) : findMatchingSchema(resolveRef, schema, discriminatorValue);

      let properties;
      if (refSchema) {
        ({ typeName, schemaPath: resolvedSchemaPath } = parseRef(refSchema.get('$ref')));
        properties = resolveRef(refSchema)?.get('properties');
      }

      return {
        isArrayItem,
        resolvedSchemaPath,
        typeName,
        properties
      };
    }

    return {
      isArrayItem,
      typeName,
      resolvedSchemaPath,
      properties: schema.get('properties')
    };
  }, [resolveRef, schema, schemaPath, discriminatorValue]);
}
